import '.ab{background-color:var(--palette-blue-50);}.ssr-plugin'
import '.f1{border-radius:0.5rem;}.ssr-plugin'
import '.ak{padding:0.5rem;}.ssr-plugin'
import { Button } from '@app/components/core/button/Button'
import { TextLink } from '@app/components/core/link/TextLink'
import { TextInput } from '@app/components/core/input/text/TextInput'
import { VerificationCodeInput } from '@app/components/core/input/text/VerificationCodeInput'
import { Text } from '@app/components/core/typography/Text'
import { Title } from '@app/components/core/typography/Title'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { composeName, emailRegex } from '@app/util/helpers'
import { pages } from '@app/util/links'
import { useAuthState, useClient } from '@based/sdk/react'
import { useTranslations } from '@i18n/useLanguage'
import { styled } from 'inlines'
import {
  useState,
  KeyboardEvent,
  useCallback,
  useEffect,
  Dispatch,
} from 'react'
import { useLocation, useSearchParams } from 'wouter'
import { SupportingInfoForm } from './blocks/SupportingInfoForm'
import { SearchSelectOption } from '@app/components/core/input/searchSelect/SearchSelect'
import { ProfileEdit } from 'functions/app/dashboard/profile/edit'
import { TermsAndConditions } from './TermsAndConditions'
import {
  clearStoredChallengeId,
  clearStoredPrefill,
  getStoredChallengeId,
  getStoredPrefill,
  OTC_CHALLENGE_QUERY_PARAM,
  OTC_QUERY_PARAM,
  setStoredPrefill,
  setStoredChallengeId,
} from './authQueryParams'
import { VerifyOneTimeCodeResult } from 'functions/app/account/verify-one-time-code'

export const SignUp = ({
  email,
  redirectTo,
  setEmail,
  onClose,
  startFromVerification = false,
}: {
  email: string
  redirectTo?: string
  setEmail: Dispatch<React.SetStateAction<string>>
  onClose?: (setUtm?: boolean) => void
  startFromVerification?: boolean
}) => {
  const client = useClient()
  const authState = useAuthState()
  const [_, navigate] = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const signupSource = searchParams.get('utm_source') ?? undefined
  const challengeIdFromUrl = searchParams.get(OTC_CHALLENGE_QUERY_PARAM) ?? ''
  const storedSignupPrefill = getStoredPrefill('signup')
  const hasSignupPrefill =
    !!storedSignupPrefill?.email &&
    !!storedSignupPrefill?.firstName &&
    !!storedSignupPrefill?.lastName
  const [challengeId, setChallengeId] = useState<string>(
    getStoredChallengeId('signup') ?? challengeIdFromUrl,
  )
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [isFirstNameInvalid, setIsFirstNameInvalid] = useState(false)
  const [isLastNameInvalid, setIsLastNameInvalid] = useState(false)
  const [isEmailInvalid, setIsEmailInvalid] = useState(false)
  const [isEmailSent, setIsEmailSent] = useState(startFromVerification)
  const [showEmailField, setShowEmailField] = useState(
    !startFromVerification || hasSignupPrefill,
  )
  const [emailIssue, setEmailIssue] = useState<string>(null)
  const [verificationCode, setVerificationCode] = useState('')
  const [isCodeCorrect, setIsCodeCorrect] = useState(null)
  const [didCreateUser, setDidCreateUser] = useState(false)
  const [showMainInfo, setShowMainInfo] = useState(true)
  const [showSupportingInfo, setShowSupportingInfo] = useState(false)
  const [supportingInfoIssue, setSupportingInfoIssue] = useState(false)
  const [residence, setResidence] = useState<SearchSelectOption>({
    key: null,
    value: '',
  })
  const [ageCategory, setAgeCategory] = useState<number | null>(null)
  const [searchRegionId, setSearchRegionId] = useState<string>(null)
  const translations = useTranslations().app.auth

  const onEmailKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      verifyEmail()
    }
  }

  const validateEmail = useCallback(() => {
    const isValid = emailRegex.test(email)
    setIsEmailInvalid(!isValid)
    return isValid
  }, [email])

  const validateFirstName = useCallback(() => {
    const isValid = firstName.trim().length > 0
    setIsFirstNameInvalid(!isValid)
    return isValid
  }, [firstName])

  const validateLastName = useCallback(() => {
    const isValid = lastName.trim().length > 0
    setIsLastNameInvalid(!isValid)
    return isValid
  }, [lastName])

  const validateForm = useCallback(() => {
    const emailValidity = validateEmail()
    const firstNameValidity = validateFirstName()
    const lastNameValidity = validateLastName()
    return emailValidity && firstNameValidity && lastNameValidity
  }, [validateEmail, validateFirstName, validateLastName])

  const verifyEmail = useCallback(() => {
    if (!validateForm()) {
      return
    }
    client
      .call('account-register', {
        email: email,
        name: composeName(firstName, lastName),
        ...(signupSource && { signupSource }),
      })
      .then((result: { challengeId?: string }) => {
        if (!result?.challengeId) {
          throw new Error('Missing challenge')
        }
        setStoredChallengeId('signup', result.challengeId)
        setStoredPrefill('signup', {
          email: email.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
        })
        setChallengeId(result.challengeId)
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev)
            next.set(OTC_QUERY_PARAM, 'signup')
            next.delete(OTC_CHALLENGE_QUERY_PARAM)
            return next
          },
          { replace: true },
        )
      })
      .catch((e: Error) => {
        setEmailIssue(
          e.message.includes('403')
            ? translations.emailIssueUserExistsMessage
            : translations.emailIssueOtherMessage,
        )
      })
    setEmailIssue(null)
    setIsEmailSent(true)
  }, [
    client,
    firstName,
    lastName,
    email,
    signupSource,
    validateForm,
    translations.emailIssueUserExistsMessage,
    translations.emailIssueOtherMessage,
    setSearchParams,
  ])

  const onSendCodeAgain = useCallback(() => {
    setIsCodeCorrect(null)
    setVerificationCode('')
    if (!showEmailField) {
      setShowEmailField(true)
      return
    }
    verifyEmail()
  }, [showEmailField, verifyEmail, setVerificationCode])

  const verifyCode = useCallback(() => {
    client
      .call('verify-one-time-code', {
        code: verificationCode,
        challengeId,
      })
      .then((result: VerifyOneTimeCodeResult) => {
        const isVerified = typeof result === 'boolean' ? result : result.success

        setDidCreateUser(
          typeof result === 'boolean' ? false : result.createdUser,
        )
        setIsCodeCorrect(isVerified)
      })
      .catch((_e: Error) => {
        setIsCodeCorrect(false)
      })
  }, [client, verificationCode, challengeId, setIsCodeCorrect])

  const addSuportingInfo = useCallback(() => {
    setSupportingInfoIssue(false)
    client
      .call('edit-profile', {
        id: authState.userId,
        value: {
          location: { id: residence.key },
          ageCategory: ageCategory ? { id: ageCategory } : null,
          searchRegions: [{ id: Number(searchRegionId) }],
        } as ProfileEdit,
      })
      .then(() => {
        if (onClose) {
          onClose(!!signupSource)
        } else {
          navigate(redirectTo ?? pages.home)
        }
      })
      .catch((_e: Error) => {
        setSupportingInfoIssue(true)
      })
  }, [
    client,
    authState,
    residence,
    ageCategory,
    searchRegionId,
    signupSource,
    redirectTo,
    navigate,
    onClose,
  ])

  const setEmailHandler = (text: string) => {
    setEmail(text)
  }

  useEffect(() => {
    const storedPrefill = getStoredPrefill('signup')
    if (!storedPrefill) {
      return
    }
    if (storedPrefill.email && !email) {
      setEmail(storedPrefill.email)
    }
    if (storedPrefill.firstName && !firstName) {
      setFirstName(storedPrefill.firstName)
    }
    if (storedPrefill.lastName && !lastName) {
      setLastName(storedPrefill.lastName)
    }
  }, [email, firstName, lastName, setEmail])

  useEffect(() => {
    if (!challengeIdFromUrl) {
      return
    }
    setStoredChallengeId('signup', challengeIdFromUrl)
    if (challengeId !== challengeIdFromUrl) {
      setChallengeId(challengeIdFromUrl)
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete(OTC_CHALLENGE_QUERY_PARAM)
        return next
      },
      { replace: true },
    )
  }, [challengeIdFromUrl, challengeId, setSearchParams])

  useEffect(() => {
    if (verificationCode.length === 6) {
      verifyCode()
    }
  }, [verificationCode, verifyCode])

  useEffect(() => {
    if (isCodeCorrect) {
      if (!didCreateUser) {
        onClose()
      }
      clearStoredChallengeId('signup')
      clearStoredPrefill('signup')
      setShowMainInfo(false)
      setShowSupportingInfo(true)
    }
  }, [isCodeCorrect, didCreateUser, onClose])

  useEffect(() => {
    if (isEmailInvalid) {
      validateEmail()
    }
  }, [email, isEmailInvalid, validateEmail])

  useEffect(() => {
    if (isFirstNameInvalid) {
      validateFirstName()
    }
  }, [firstName, isFirstNameInvalid, validateFirstName])

  useEffect(() => {
    if (isLastNameInvalid) {
      validateLastName()
    }
  }, [lastName, isLastNameInvalid, validateLastName])

  const emailSentSuccessfully = isEmailSent && !emailIssue

  const supportingInfoFilled = residence.key && ageCategory && searchRegionId

  return (
    <Column alignItems="stretch" style={{}}>
      {showMainInfo && (
        <>
          <Title.H3 style={{ alignSelf: 'center' }}>
            {translations.signUpTitle}
          </Title.H3>

          <Text.Large style={{ alignSelf: 'center' }}>
            {translations.signUpSubtitle}
          </Text.Large>

          {showEmailField && (
            <Row style={{ flexWrap: 'wrap' }}>
              <TextInput
                label={translations.firstName}
                placeholder={translations.firstNamePlaceholder}
                text={firstName}
                errorMessage={
                  isFirstNameInvalid ? translations.nameInvalidMessage : null
                }
                setText={setFirstName}
                style={{
                  flex: '1 1 0',
                }}
              />
              <TextInput
                label={translations.lastName}
                placeholder={translations.lastNamePlaceholder}
                text={lastName}
                errorMessage={
                  isLastNameInvalid ? translations.nameInvalidMessage : null
                }
                setText={setLastName}
                style={{
                  flex: '1 1 0',
                }}
              />
            </Row>
          )}

          {showEmailField && (
            <TextInput
              label={translations.email}
              placeholder={translations.emailPlaceholder}
              text={email}
              errorMessage={
                isEmailInvalid ? translations.emailInvalidMessage : null
              }
              setText={setEmailHandler}
              onKeyDown={onEmailKeyDown}
            />
          )}

          {!emailIssue && !isEmailSent && <TermsAndConditions />}
          {emailIssue && (
            <Text.Medium isBold={true} color={'var(--brand-primary)'}>
              {emailIssue}
            </Text.Medium>
          )}

          {emailSentSuccessfully && (
            <Column alignItems="stretch" style={{ flexGrow: 0 }}>
              <div className="ab f1 ak"
              >
                <Text.Medium isBold={true} color={'var(--palette-blue-500)'}>
                  {translations.verificationCodeBanner}
                </Text.Medium>{' '}
                <TextLink onClick={onSendCodeAgain} size="medium">
                  {translations.sendCodeAgain}
                </TextLink>
              </div>
              <VerificationCodeInput
                disabled={isCodeCorrect}
                text={verificationCode}
                errorMessage={
                  isCodeCorrect === false ? translations.otcIssueMessage : null
                }
                setText={setVerificationCode}
              />
            </Column>
          )}
        </>
      )}

      {showSupportingInfo && (
        <>
          {!showMainInfo && (
            <Column alignItems="stretch" style={{ flex: 0 }}>
              <Title.H3 style={{ alignSelf: 'center' }}>
                {translations.shareMoreTitle}
              </Title.H3>
              <Text.Medium>{translations.shareMoreSubtitle}</Text.Medium>
            </Column>
          )}

          <SupportingInfoForm
            ageCategory={ageCategory}
            residence={residence}
            searchRegionId={searchRegionId}
            setResidence={setResidence}
            setAgeCategory={setAgeCategory}
            setSearchRegionId={setSearchRegionId}
          />

          {supportingInfoIssue && (
            <Text.Medium isBold={true} color={'var(--brand-primary)'}>
              {translations.otherIssueMessage}
            </Text.Medium>
          )}
        </>
      )}

      <Column alignItems="stretch" style={{ justifyContent: 'end' }}>
        {showMainInfo && (
          <Button
            disabled={isEmailInvalid || isFirstNameInvalid || isLastNameInvalid}
            label={translations.signUpButton}
            onClick={emailSentSuccessfully ? verifyCode : verifyEmail}
          />
        )}

        {showSupportingInfo && (
          <Button
            disabled={!supportingInfoFilled}
            label={translations.signUpButton}
            onClick={addSuportingInfo}
          />
        )}
      </Column>
    </Column>
  )
}
